import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { io } from 'socket.io-client';

const MAP_CONTAINER_STYLE = { width: '100%', height: '400px', borderRadius: '8px' };
const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

/**
 * LivePatrolTab — owns the websocket connection for real-time officer locations.
 *
 * Props:
 *   shift  — the currently selected shift object (from shiftDetail.shift)
 *
 * Socket room: shift_${shift._id}  (NOT job_${job._id})
 * This ensures location updates are scoped to the specific active shift.
 * When the user selects a different shift, shift._id changes, the useEffect
 * cleanup leaves the old room and the new effect joins the new room.
 */
const LivePatrolTab = ({ shift, job }) => {
  const [locationUpdates, setLocationUpdates] = useState([]);
  const [connected, setConnected]             = useState(false);
  const [connectionLogs, setConnectionLogs]   = useState([]);
  const [activeView, setActiveView]           = useState('live');
  const [officerPaths, setOfficerPaths]       = useState({});
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const mapRef = useRef();

  // GeoJSON is [lng, lat] — Google Maps needs { lat, lng }
  const jobCenter = job?.geoLocation?.coordinates
    ? { lat: job.geoLocation.coordinates[1], lng: job.geoLocation.coordinates[0] }
    : null;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    if (jobCenter) {
      map.panTo(jobCenter);
      map.setZoom(13);
    }
  }, [jobCenter]);

  const logEvent = (message) => {
    console.log(message);
    setConnectionLogs(prev => [`${new Date().toLocaleTimeString()}: ${message}`, ...prev.slice(0, 9)]);
  };

  const createMarkerIcon = (isActive) => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="${isActive ? '#4CAF50' : '#9E9E9E'}" stroke="#fff" stroke-width="3"/>
        <circle cx="20" cy="20" r="6" fill="#fff"/>
        ${isActive ? '<circle cx="20" cy="20" r="3" fill="#2E7D32"/>' : ''}
      </svg>`)}`,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor:     new window.google.maps.Point(20, 20),
  });

  // Re-runs whenever the selected shift changes → leaves old room, joins new one
  useEffect(() => {
    if (!shift?._id) return;

    // Reset state for the new shift
    setLocationUpdates([]);
    setOfficerPaths({});
    setSelectedOfficer(null);

    const roomId = `shift_${shift._id}`;

    const socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001/location', {
      withCredentials: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      logEvent('Connected to location server');
      setConnected(true);
      setTimeout(() => {
        socket.emit('joinShiftRoom', { roomId, shiftId: shift._id });
        logEvent(`Joined room: ${roomId}`);
      }, 300);
    });

    socket.on('roomJoined', (data) => logEvent(`Room confirmed: ${data.roomId}`));

    socket.on('locationUpdate', (data) => {
      const update = {
        ...data,
        timestamp:  data.location?.timestamp || data.timestamp || new Date(),
        lat:        data.location?.latitude  || data.latitude  || data.lat,
        lng:        data.location?.longitude || data.longitude || data.lng,
        accuracy:   data.location?.accuracy  || data.accuracy,
        officerId:  data.user?._id || data.officerId || data.userId || 'Unknown',
        officerName:  data.user ? `${data.user.firstName} ${data.user.lastName}` : undefined,
        officerEmail: data.user?.email,
        shiftId: data.shiftId,
        offerId: data.offerId,
      };

      setLocationUpdates(prev => [update, ...prev.slice(0, 19)]);
      setOfficerPaths(prev => {
        const path = prev[update.officerId] || [];
        return {
          ...prev,
          [update.officerId]: [
            ...path,
            { lat: update.lat, lng: update.lng, timestamp: update.timestamp },
          ].slice(-50),
        };
      });
    });

    socket.on('connect_error', (err) => { logEvent(`Connection error: ${err}`); setConnected(false); });
    socket.on('disconnect', (reason) => { logEvent(`Disconnected: ${reason}`); setConnected(false); });

    return () => {
      socket.emit('leaveShiftRoom', { roomId, shiftId: shift._id });
      logEvent(`Left room: ${roomId}`);
      socket.disconnect();
    };
  }, [shift?._id]);

  const fmtTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getActiveOfficers = () => {
    const map = new Map();
    locationUpdates.forEach(u => {
      if (!map.has(u.officerId) || new Date(u.timestamp) > new Date(map.get(u.officerId).timestamp)) {
        map.set(u.officerId, u);
      }
    });
    return Array.from(map.values());
  };

  const isActive = (ts) => (Date.now() - new Date(ts).getTime()) < 5 * 60 * 1000;
  const activeOfficers = getActiveOfficers();

  return (
    <div className="font-inter space-y-4">
      {/* Header */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#404880]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h6 className="text-sm font-semibold text-[#404880]">Real-time Location</h6>
            {shift && (
              <span className="text-xs text-muted-foreground">
                — Shift {new Date(shift.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
              {['live', 'logs'].map(v => (
                <button
                  key={v}
                  onClick={() => setActiveView(v)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                    activeView === v ? 'bg-[#404880] text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {v === 'live' ? 'Live Feed' : 'Logs'}
                </button>
              ))}
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${connected ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
              {connected ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
        </div>
      </div>

      {/* Logs view */}
      {activeView === 'logs' && (
        <div className="space-y-2">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 max-h-32 overflow-auto">
            <p className="text-xs font-semibold text-[#404880] mb-1">Connection Logs</p>
            {connectionLogs.map((log, i) => (
              <p key={i} className="text-xs font-mono text-gray-600 pl-2 leading-relaxed">{log}</p>
            ))}
          </div>
          {locationUpdates.length > 0 ? (
            locationUpdates.map((u, i) => (
              <div key={i} className="p-3 bg-white border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <p className="text-sm font-medium text-[#404880]">{u.officerName || u.officerId}</p>
                  <p className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{fmtTime(u.timestamp)}</p>
                </div>
                {u.officerEmail && <p className="text-xs text-gray-500 mt-0.5">{u.officerEmail}</p>}
                <p className="text-xs text-gray-700 mt-1 p-1.5 bg-gray-50 border border-gray-200 rounded font-mono">
                  {u.lat?.toFixed(6)}, {u.lng?.toFixed(6)}
                  {u.accuracy && ` · ±${Math.round(u.accuracy)}m`}
                </p>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center h-24 bg-white border border-dashed border-gray-300 rounded-lg">
              <p className="text-sm text-gray-400 italic">No updates received yet</p>
            </div>
          )}
        </div>
      )}

      {/* Live map view */}
      {activeView === 'live' && (
        <div className="space-y-3">
          {/* Active officers pills */}
          <div className="p-3 bg-white rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-[#404880] mb-2">
              Active Officers ({activeOfficers.filter(o => isActive(o.timestamp)).length})
            </p>
            <div className="flex flex-wrap gap-2">
              {activeOfficers.map(o => (
                <button
                  key={o.officerId}
                  onClick={() => { if (mapRef.current) { mapRef.current.panTo({ lat: o.lat, lng: o.lng }); mapRef.current.setZoom(16); } }}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    isActive(o.timestamp) ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive(o.timestamp) ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  {o.officerName || o.officerId}
                </button>
              ))}
              {activeOfficers.length === 0 && (
                <p className="text-xs text-gray-400 italic">No officers online</p>
              )}
            </div>
          </div>

          {/* Map */}
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={jobCenter || DEFAULT_CENTER}
              zoom={13}
              onLoad={onLoad}
              options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
            >
              {/* Static pin at the job's address */}
              {jobCenter && (
                <Marker
                  position={jobCenter}
                  title={job?.formattedAddress || job?.location || 'Job location'}
                />
              )}
              {activeOfficers.map(o => (
                <Marker
                  key={o.officerId}
                  position={{ lat: o.lat, lng: o.lng }}
                  icon={createMarkerIcon(isActive(o.timestamp))}
                  onClick={() => setSelectedOfficer(o)}
                  animation={isActive(o.timestamp) ? window.google.maps.Animation.BOUNCE : null}
                />
              ))}
              {Object.entries(officerPaths).map(([id, path]) => (
                <Polyline key={id} path={path} options={{ strokeColor: '#2196F3', strokeOpacity: 0.8, strokeWeight: 3 }} />
              ))}
              {selectedOfficer && (
                <InfoWindow position={{ lat: selectedOfficer.lat, lng: selectedOfficer.lng }} onCloseClick={() => setSelectedOfficer(null)}>
                  <div className="p-1.5">
                    <p className="text-sm font-semibold">{selectedOfficer.officerName || selectedOfficer.officerId}</p>
                    {selectedOfficer.officerEmail && <p className="text-xs text-gray-500">{selectedOfficer.officerEmail}</p>}
                    <p className="text-xs text-gray-400 mt-1">Last update: {fmtTime(selectedOfficer.timestamp)}</p>
                    {selectedOfficer.accuracy && <p className="text-xs text-gray-400">Accuracy: ±{Math.round(selectedOfficer.accuracy)}m</p>}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-96 bg-white border border-dashed border-gray-300 rounded-lg">
              <p className="text-sm text-gray-400 italic">Loading Google Maps…</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LivePatrolTab;
